import { Route, useParams, withRouter } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import qs from 'qs';
import { get } from './utils/http';
import { filterPublishedContent } from './utils/pageUtils';
import Routes from './routes/Routes';
import { PageDataContext } from './context/PageDataContext';
import { extractCollection } from './utils/strapi';
import './assets/vendor/bootstrap-3.3.6-dist/css/bootstrap.min.css';
import './assets/vendor/font-awesome-4.6.3/css/font-awesome.min.css';
import './assets/sass/vertical-rhythm.scss';
import './assets/sass/App.scss';

const PAGE_SUMMARY_FIELDS = [
  'slug',
  'name',
  'is_home',
  'is_category_grid_page',
  'show_in_home',
  'order_value',
  'is_published',
  'locale',
];

const PAGE_SUMMARY_POPULATE = {
  category: {
    fields: ['slug', 'name', 'documentId'],
  },
  meta_image: {
    fields: ['url', 'formats', 'alternativeText'],
  },
};

const PAGE_DETAIL_FIELDS = [
  'slug',
  'name',
  'meta_description',
  'is_home',
  'is_category_grid_page',
  'order_value',
  'show_in_home',
  'is_published',
  'locale',
];

const PAGE_DETAIL_POPULATE = {
  category: {
    fields: ['slug', 'name', 'documentId'],
  },
  meta_image: {
    fields: ['url', 'formats', 'alternativeText'],
  },
  rows: {
    on: {
      'block-type.image-text': {
        populate: {
          image: {
            populate: {
              media: {
                fields: ['url', 'formats', 'alternativeText', 'width', 'height'],
              },
            },
          },
          text: {
            fields: ['text', 'is_full_width'],
          },
        },
      },
      'block-type.image-image': {
        populate: {
          images: {
            populate: {
              media: {
                fields: ['url', 'formats', 'alternativeText', 'width', 'height'],
              },
            },
          },
        },
      },
      'block-type.text-text': {
        populate: {
          texts: {
            fields: ['text', 'is_full_width'],
          },
        },
      },
      'block-type.text-video': {
        populate: {
          text: {
            fields: ['text', 'is_full_width'],
          },
          video: true,
        },
      },
      'block-type.image': {
        populate: {
          media: {
            fields: ['url', 'formats', 'alternativeText', 'width', 'height'],
          },
        },
      },
      'block-type.text': {
        fields: ['text', 'is_full_width'],
      },
      'visual-components.slider': {
        populate: {
          slides: {
            fields: ['url', 'formats', 'alternativeText', 'width', 'height'],
          },
        },
      },
      'visual-components.map': {
        fields: ['lat', 'lng'],
      },
    },
  },
};

function App() {
  const locales = [{ locale: 'en' }, { locale: 'lv' }, { locale: 'ru' }];
  const [pageSummaries, setPageSummaries] = useState([]);
  const [pageNavbar, setPageNavbar] = useState([]);
  const [pageLocales, setPageLocales] = useState(locales);
  const [currentLocale, setCurrentLocale] = useState('');
  const { locale } = useParams();
  const pageCacheRef = useRef({});

  useEffect(() => {
    const loadData = async () => {
      const summaries = await fetchPageSummaries();
      setPageSummaries(summaries);
      const navbarData = await fetchPageNavbar();
      setPageNavbar(navbarData);
    };

    const updatedLocales = addCurrentLocale(locale, pageLocales);
    setPageLocales(updatedLocales);
    setCurrentLocale(locale);
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const fetchPageSummaries = useCallback(async () => {
    try {
      const query = qs.stringify(
        {
          locale,
          publicationState: 'live',
          pagination: {
            pageSize: 200,
          },
          fields: PAGE_SUMMARY_FIELDS,
          populate: PAGE_SUMMARY_POPULATE,
        },
        {
          encodeValuesOnly: true,
        }
      );

      const resp = await get(`${process.env.REACT_APP_CDP_API_URL}/api/pages?${query}`);
      const summaries = filterPublishedContent(extractCollection(resp));
      console.log(`App: Loaded ${summaries.length} page summaries`);
      return summaries;
    } catch (error) {
      console.error('Failed to fetch page summaries', error);
      return [];
    }
  }, [locale]);

  const fetchPageNavbar = useCallback(async () => {
    const query = qs.stringify(
      {
        locale,
        publicationState: 'live',
        populate: {
          menu: {
            on: {
              'menu.item': {
                populate: {
                  page: true,
                },
              },
            },
          },
        },
      },
      {
        encodeValuesOnly: true,
      }
    );

    const resp = await get(`${process.env.REACT_APP_CDP_API_URL}/api/menu?${query}`).catch(error => {
      console.log('Menu fetch error:', error);
      return get(`${process.env.REACT_APP_CDP_API_URL}/api/menu?locale=${locale}&populate=*`).catch(() => ({ data: { data: { menu: [] } } }));
    });

    return resp?.data?.data || resp?.data || {};
  }, [locale]);

  const getPageData = useCallback(async ({ locale: targetLocale, slug, parentslug, isHome }) => {
    const cacheKey = JSON.stringify({ locale: targetLocale, slug, parentslug, isHome });
    if (pageCacheRef.current[cacheKey]) {
      return pageCacheRef.current[cacheKey];
    }

    const filters = {};
    if (isHome) {
      filters.is_home = { $eq: true };
    } else {
      filters.slug = { $eq: slug };
      if (parentslug) {
        filters.category = { slug: { $eq: parentslug } };
      }
    }

    const query = qs.stringify(
      {
        locale: targetLocale,
        publicationState: 'live',
        fields: PAGE_DETAIL_FIELDS,
        filters,
        populate: PAGE_DETAIL_POPULATE,
      },
      {
        encodeValuesOnly: true,
      }
    );

    try {
      const resp = await get(`${process.env.REACT_APP_CDP_API_URL}/api/pages?${query}`);
      const pages = filterPublishedContent(extractCollection(resp));
      pageCacheRef.current[cacheKey] = pages;
      return pages;
    } catch (error) {
      console.error('Failed to fetch page detail', { locale: targetLocale, slug, parentslug, error });
      return [];
    }
  }, []);

  const addCurrentLocale = (locale, pageLocales) => {
    pageLocales.map(pageLocale => {
      if (pageLocale.locale === locale) {
        pageLocale.is_current_locale = true;
        return pageLocale;
      }
      return pageLocale;
    });
    return pageLocales;
  };

  const contextValue = useMemo(
    () => ({
      getPageData,
    }),
    [getPageData]
  );

  return (
    <PageDataContext.Provider value={contextValue}>
      <Route path="/:locale">
        {pageNavbar.menu ? (
          <Routes
            pageSummaries={pageSummaries}
            pageNavbar={pageNavbar}
            pageLocales={pageLocales}
            currentLocale={currentLocale}
          />
        ) : (
          ''
        )}
      </Route>
    </PageDataContext.Provider>
  );
}

export default withRouter(App);
